import type { CotacaoEnvioAr } from '@/types/arbitral/envioArTypes';
import type { CertificadoAcessosArbitral } from '@/features/arbitral-detalhes';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { datadogRum } from '@datadog/browser-rum';

import { ConflitoIdempotenciaError } from '@/features/secretaria-arbitral/utils/conflitoIdempotencia';
import { diasDeRetroatividade } from '@/features/secretaria-arbitral/utils/manifestacoes';
import {
  apresentarMotivoLote,
  motivoAcaoLegivel,
} from '@/features/secretaria-arbitral/utils/motivoLoteLabels';
import { hojeEmSaoPaulo } from '@/features/secretaria-arbitral/utils/prazoAndamento';
import {
  CODIGOS_SOLICITACAO_AR,
  CotacaoDefasadaError,
} from '@/features/secretaria-arbitral/utils/solicitacaoArErros';
import { ehErroHttp, temCodigoHttp } from '@/shared/utils/erroHttp';
import { mesmoIdentificador } from '@/features/secretaria-arbitral/utils/triagemSelecao';
import { ehUuid } from '@/shared/utils/uuid';
import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';

import type {
  AdicionarAdvogadoParteInput,
  AdvogadoDaParteItem,
  AlterarAdvogadoResultado,
  AlterarSituacaoParteResultado,
  AndamentoArbitralItem,
  DiasNaoUteisDoAno,
  ModeloAndamentoOpcao,
  PublicarAndamentoInput,
  PublicarAndamentoResultado,
  TextoModeloMaterializado,
  AnexoUploadEnviado,
  CanalAcessoParte,
  ReenviarLinkAcessoResultado,
  VerificacaoFacialParteInput,
  VerificacaoFacialParteResultado,
  ArbitralAlertaDispensaMotivo,
  ArbitralAlertasAtivosPagina,
  ListarAlertasAtivosParams,
  ArDetalhe,
  ConsultaDocumentoParteResultado,
  CriarParteArbitralInput,
  CriarParteArbitralResultado,
  EditarAdvogadoParteInput,
  EditarParteArbitralInput,
  EditarParteArbitralResultado,
  RemoverAdvogadoResultado,
  ArAEnviarFiltros,
  ArAEnviarResult,
  RequeridoAEnviarDetalhe,
  ArListFiltros,
  ArListItem,
  ArListResult,
  ReemitirCobrancaArResultado,
  SolicitarEnvioArPayload,
  SolicitarEnvioArResultado,
  AuditoriaTriagemPage,
  ChecklistTriagemAvaliacaoInput,
  CertificadoArbitralItem,
  ChatListPage,
  ChecklistTriagem,
  OrigemAvaliacaoChecklist,
  ConclusoInput,
  ConclusoResult,
  CriarManifestacaoInput,
  DocumentoArbitralItem,
  ExportarProcessosResultado,
  ListProcessosFilters,
  ListProcessosResponse,
  LoteExecucaoErro,
  LoteExecucaoResponse,
  LoteRequestInput,
  LoteValidacaoInput,
  LoteValidacaoResponse,
  ManifestacaoListItem,
  PainelGeralCounts,
  ParteCompletaItem,
  CriarPendenciaPayload,
  CriarPendenciaResultado,
  ListarPendenciasFilters,
  ListarPendenciasSecretariaResult,
  MovimentarPendenciasPayload,
  MovimentarPendenciasResultado,
  PrazoPendenciaPreview,
  ResultadoLoteTriagem,
  TriagemDecisaoAgendada,
  TriagemDecisaoAtiva,
  PrazosPainelData,
  PrazosPainelQuery,
  ProcessoArbitralListItem,
  ProcessoArbitralStatus,
  ProcessoDetalhe,
  ProcessosFacets,
  PublicarDecursoBody,
  PublicarDecursoResult,
  SituacaoTriagemApi,
  PublicarTransitoBody,
  PublicarTransitoResult,
  TransitoElegibilidade,
  RankingArbitroItem,
  LiberarSentencaBody,
  LiberarSentencaResult,
  ReabrirProcessoBody,
  ReabrirProcessoResult,
  ResumoTriagem,
  SorteioBody,
  SorteioResult,
  SorteioSugestao,
  TriagemDecisao,
  TriagemDecisaoLote,
  PrazoDaRecusa,
  ValidacaoPendenciaAcao,
  ValidarPendenciaResult,
} from '@/features/secretaria-arbitral/types';
// Valor, não tipo — import separado de propósito (o bloco acima é `import type`).
import { ORIGEM_TRIAGEM } from '@/features/secretaria-arbitral/types';
import type {
  ConcluirSolicitacaoInput,
  ConcluirSolicitacaoResultado,
  DecidirSolicitacaoInput,
  DecidirSolicitacaoResultado,
  FiltroSolicitacoes,
  SolicitacoesSecretariaResposta,
} from '@/features/secretaria-arbitral/types/solicitacoes';
import { dividirEmLotes } from '@/shared/utils/batchDispatch';
import { reportError } from '@/shared/utils/reportError';

import { selectFiltroPendencias } from './selectors';
import { secretariaArbitralService } from './services';

const LOTE_EXPORTACAO_AUDITORIA = 500;

export const fetchPainelGeralCounts = createAsyncThunk<PainelGeralCounts>(
  'secretariaArbitral/fetchPainelGeralCounts',
  async () => secretariaArbitralService.obterPainelGeralCounts(),
);

export const fetchProcessosPorStatus = createAsyncThunk<
  { key: string; items: ProcessoArbitralListItem[] },
  { key: string; status: ProcessoArbitralStatus[]; limit?: number }
>('secretariaArbitral/fetchProcessosPorStatus', async ({ key, status, limit }) => {
  const items = await secretariaArbitralService.listarProcessosPorStatus(status, limit);
  return { key, items };
});

export const fetchProcessos = createAsyncThunk<ListProcessosResponse, ListProcessosFilters>(
  'secretariaArbitral/fetchProcessos',
  async (filters) => secretariaArbitralService.listarProcessos(filters),
);

export const fetchProcessosFacets = createAsyncThunk<ProcessosFacets, ListProcessosFilters>(
  'secretariaArbitral/fetchProcessosFacets',
  async (filters) => secretariaArbitralService.obterProcessosFacets(filters),
);

export const FILA_TRIAGEM_LIMIT = 100;

/** Teto da busca por NÚMERO do caso pedido (o do endpoint é 200). */
const BUSCA_ALVO_LIMIT = 100;

/** Piso de tamanho para o alvo valer paginação — ver `buscarAlvoPorNumero`. */
const TAMANHO_MINIMO_ALVO = 8;

/**
 * Busca o caso pedido pelo NÚMERO, página a página, até o casamento exato.
 *
 * Exato, e não `contains`: o servidor já filtrou por substring, e adotar o
 * primeiro que voltasse abriria o processo errado — um prefixo de outro número.
 *
 * Sem teto de PÁGINAS para um alvo com cara de identificador: o laço termina no
 * `total` que o próprio servidor informa, ou numa página vazia. Um teto de páginas
 * reintroduziria, mais longe, o defeito que este laço existe para matar — a tela
 * dizendo "não foi possível abrir este caso" sobre um caso que existe, só que a
 * partir do 501º em vez do 101º.
 *
 * O que limita é o TAMANHO do alvo, não a contagem de páginas: abaixo de
 * `TAMANHO_MINIMO_ALVO` a busca fica na primeira página (ver o `vaiPaginar`
 * abaixo). Um fragmento curto não é deep-link — `?processo=` carrega id ou número
 * completo —, e é só ele que poderia varrer o resultado inteiro.
 *
 * O custo cresce com quantas LINHAS casam o termo — e `search` casa por número,
 * nome de parte e CPF/CNPJ, não só por número —, não com o tamanho da fila: um
 * número de processo inteiro casa com ~1 linha e resolve na primeira página.
 * Um `?processo=` curto o bastante para casar com milhares não é deep-link válido
 * (o param carrega id ou número completo) — é operador autenticado montando URL à
 * mão, não superfície pública.
 */
async function buscarAlvoPorNumero(alvo: string): Promise<ProcessoArbitralListItem | null> {
  // Um fragmento curto não é deep-link: `?processo=` carrega id ou número
  // completo. Sem este piso, `?processo=1` percorreria o resultado inteiro do
  // `contains` — e `search` casa por número, nome de parte E documento, então
  // "1" casa com quase tudo. Não é checagem de FORMATO (numeroProcesso é input
  // livre e o formato varia): é o piso que separa identificador de fragmento, e
  // abaixo dele a primeira página basta para o casamento exato existir ou não.
  const vaiPaginar = alvo.trim().length >= TAMANHO_MINIMO_ALVO;
  let pagina = 1;
  let total = Infinity;
  while ((pagina - 1) * BUSCA_ALVO_LIMIT < total) {
    const resposta = await secretariaArbitralService.listarProcessos({
      search: alvo,
      // Ramo por NÚMERO do mesmo deep-link: mesmo recorte do ramo por id, pela mesma
      // razão registrada lá.
      origem: ORIGEM_TRIAGEM,
      page: pagina,
      limit: BUSCA_ALVO_LIMIT,
      // Ordenação FIXA: sem ela as linhas devolvidas não são estáveis entre
      // tentativas, e paginar sobre ordem indefinida pode ver a mesma linha duas
      // vezes e nunca ver outra.
      sortBy: 'dataEntrada',
      sortOrder: 'asc',
    });
    total = resposta.total;
    const exato = resposta.items.find((i) => mesmoIdentificador(i.numeroProcesso, alvo));
    if (exato) return exato;
    if (resposta.items.length === 0 || !vaiPaginar) break;
    pagina += 1;
  }
  return null;
}

/**
 * UMA página da fila do painel, sob demanda (web#1063).
 *
 * Antes este thunk iterava até 20 páginas para montar a fila inteira em
 * memória: abrir o painel custava até 20 round-trips sequenciais — com 8 casos
 * na fila também, porque o custo crescia com a base e não com o trabalho — e
 * passando de 2.000 o caso pedido ficava fora da janela e a tela dizia que não
 * havia processos aguardando triagem.
 */
export const fetchFilaTriagem = createAsyncThunk<ListProcessosResponse, ListProcessosFilters>(
  'secretariaArbitral/fetchFilaTriagem',
  async (filters) => {
    const { page, ...semPagina } = filters;
    const porCursor = typeof filters.cursor === 'string' && filters.cursor.length > 0;
    const comum = {
      ...semPagina,
      // DEPOIS do spread de propósito: o recorte é da SUPERFÍCIE, não do chamador —
      // assim um caller futuro não consegue esquecê-lo nem afrouxá-lo.
      origem: ORIGEM_TRIAGEM,
      limit: filters.limit ?? FILA_TRIAGEM_LIMIT,
    };
    // A chave `page` some da requisição com cursor — não vai com `1`. No core,
    // `page > 1` junto de `cursor` é 400, e mandar posição e fronteira na mesma
    // pergunta é pedir que o servidor desempate entre duas paginações.
    const pagina = await secretariaArbitralService.listarProcessos(
      porCursor ? comum : { ...comum, page: page ?? 1 },
    );
    // OBS-01: o defeito foi invisível porque ninguém media o tamanho da fila que
    // o painel enxergava. `total` aqui é o da fila inteira, não o da janela.
    //
    // ⚠️ O VALOR do cursor não entra neste payload: ele carrega `dataEntrada` e o id de
    // um processo, e uma ação customizada não é lugar para nenhum dos dois. (A query da
    // requisição carrega o cursor como qualquer outro identificador opaco do app — ver
    // `datadogRumRedaction.ts`, que mantém `id`/`processoId` de propósito.) O que se mede
    // é a adoção (`porCursor`), o suporte do servidor (`suporteCursor`/`suporteAnteriores`)
    // e se há continuação (`temProxima`).
    //
    // `suporteAnteriores` pergunta se a CHAVE veio, não se o número é maior que zero:
    // `anteriores: 0` é a primeira página de um core que SUPORTA o campo, e colapsar os
    // dois casos apagaria justamente o sinal de core sem `anteriores` (EDGE-21) — a
    // mesma distinção que separa `suporteCursor` de `temProxima`. O nome diz a pergunta.
    datadogRum.addAction('triagem_fila_pagina_carregada', {
      porCursor,
      suporteCursor: pagina.nextCursor !== undefined,
      temProxima: !!pagina.nextCursor,
      suporteAnteriores: typeof pagina.anteriores === 'number',
      limit: pagina.limit,
      total: pagina.total,
      itens: pagina.items.length,
      ...(porCursor ? {} : { page: pagina.page }),
    });
    return pagina;
  },
);

/**
 * O caso pedido, quando ele NÃO está na página carregada (REQ-06).
 *
 * Vai à listagem por `processoId` — e não ao endpoint de detalhe — porque o card
 * da fila é montado a partir do item da LISTA (`paraProcessoTriagem`) e depende
 * de `situacaoTriagem`, `decisaoAtiva`, `extracaoStatus` e `iaConfiante`, que
 * nascem no enriquecimento da listagem no core e não existem no detalhe.
 *
 * Sem `status` de propósito: o alvo pode já estar TRIADO (E-05), e no core o
 * fragmento por id compõe por AND — um `status` da fila junto o esconderia.
 */
export const fetchCasoTriagemAlvo = createAsyncThunk<
  { alvo: string; item: ProcessoArbitralListItem | null },
  { alvo: string }
>('secretariaArbitral/fetchCasoTriagemAlvo', async ({ alvo }) => {
  // E-09: `?processo=` aceita id OU número desde o #1064. `processoId` é tipado
  // como UUID no core, então mandar um número ali faria o Zod recusar a query
  // inteira — número vai por `search`, que o servidor resolve por
  // `numeroProcesso contains`. Por ser `contains`, o casamento é reconferido
  // aqui: prefixo de outro processo não pode virar "o caso pedido".
  const porId = ehUuid(alvo);
  let porNumero: ProcessoArbitralListItem | null = null;
  let resposta;
  try {
    if (porId) {
      resposta = await secretariaArbitralService.listarProcessos({
        processoId: alvo,
        // ⚠️ Única exceção ao contrato do `processoId`, que manda OMITIR os demais
        // recortes. `status` esconderia um caso DA PLATAFORMA já triado — que é o que
        // este caminho existe para abrir; `origem` esconde o que a triagem não deve
        // abrir de jeito nenhum. Sem ela, deep-link para processo do Bubble ofereceria
        // botões de decisão para um caso fora do ciclo. (O core registra a mesma
        // exceção no comentário do validator.)
        origem: ORIGEM_TRIAGEM,
        page: 1,
        limit: 1,
      });
    } else {
      porNumero = await buscarAlvoPorNumero(alvo);
    }
  } catch (error: unknown) {
    // A mensagem crua é diagnóstico, não microcopy: fica aqui e NÃO vai para a
    // tela, que distingue "consulta falhou" de "caso não existe" com texto
    // redigido. Sem este registro, a falha de transporte ficaria indistinguível
    // no RUM do caso legitimamente inexistente.
    datadogRum.addError(error, { origem: 'triagem_caso_alvo', porId });
    throw error;
  }
  // Os DOIS ramos reconferem o que voltou. Aceitar `items[0]` no ramo por id
  // parecia seguro porque o servidor recorta em 1 linha — mas é o servidor que
  // decide isso, e um schema Zod sem `.strict()` DESCARTA em silêncio o param
  // que não conhece. Se `processoId` for ignorado (core#1665 ainda não está em
  // `develop`, ou o param é renomeado no futuro), a listagem volta com a
  // primeira página da fila e `items[0]` viraria "o caso pedido".
  //
  // O que isso produz, medido e não suposto: o efeito que pousa a seleção casa
  // por id/numero, então o processo errado NÃO é aberto — ele entra no conjunto
  // de trabalho do painel (`itens`, via `casoTriagemAlvo.item`), de onde
  // `proximoCaso` pode alcançá-lo depois de uma decisão. Seleção fora da ordem
  // da fila, não decisão sobre o processo errado. Com a reconferência, o pior
  // caso vira a tela dizer que não conseguiu abrir (REQ-08), que é verdade.
  const item = porId
    ? (resposta?.items.find((i) => mesmoIdentificador(i.id, alvo)) ?? null)
    : porNumero;
  // OBS-02: o recorte por origem acrescentou uma causa LEGÍTIMA a este contador —
  // deep-link para processo do Bubble, que a triagem não deve abrir. Ele soma-se às
  // antigas (id/número inexistente, apagado, de outro tenant), e daqui não dá para
  // separá-las: a listagem responde "0 linhas" sem dizer por quê, e perguntar de novo
  // sem o recorte só para rotular o motivo traria de volta o processo que a regra
  // acabou de excluir. Por isso `NAO_ENCONTRADO` continua sendo tudo o que se pode
  // afirmar. 🔴 Consequência para quem vigia: a BASELINE deste contador sobe com o
  // deploy deste filtro, e subir não é mais sinal de regressão — o que vale comparar é
  // a série DEPOIS da virada. Quem separa os dois mundos é a métrica do core
  // (`arbitral_processos_listagem`, dimension `origem`), não esta ação.
  if (item === null) {
    datadogRum.addAction('triagem_caso_fora_da_janela', { porId, motivo: 'NAO_ENCONTRADO' });
  }
  return { alvo, item };
});

/**
 * Busca da lista lateral do painel, no SERVIDOR (REQ-07).
 *
 * Slot próprio, e não o da fila: filtrar a fila pelo termo digitado trocaria o
 * dataset do painel inteiro e o caso aberto sairia da lista no meio da digitação.
 * O servidor já busca por número, nome de parte e CPF/CNPJ (`searchWhere`), o
 * que o filtro em memória sobre a janela nunca cobriu.
 */
export const buscarNaFilaTriagem = createAsyncThunk<
  { termo: string; items: ProcessoArbitralListItem[]; total: number },
  { termo: string; status: ProcessoArbitralStatus[] }
>('secretariaArbitral/buscarNaFilaTriagem', async ({ termo, status }) => {
  const resposta = await secretariaArbitralService.listarProcessos({
    search: termo,
    status,
    // Busca DENTRO da fila do painel: mesmo recorte do dataset que ela filtra, ou o
    // `total` desta lista passaria a falar de um universo maior que o da fila.
    origem: ORIGEM_TRIAGEM,
    sortBy: 'dataEntrada',
    sortOrder: 'asc',
    page: 1,
    limit: FILA_TRIAGEM_LIMIT,
  });
  // `total` vem junto porque a lista precisa dizer quando CORTOU. Buscar por um
  // nome de parte frequente devolve 100 linhas, e sem esse número a tela afirma
  // sobre o mundo o que sabe só sobre o recorte que carregou — a mesma família de
  // defeito que este PR está matando, no caminho vizinho ao que já a resolve.
  return { termo, items: resposta.items, total: resposta.total };
});

export const fetchListaTriagem = createAsyncThunk<ListProcessosResponse, ListProcessosFilters>(
  'secretariaArbitral/fetchListaTriagem',
  async (filters) => secretariaArbitralService.listarProcessos(filters),
);

export const fetchResumoTriagem = createAsyncThunk<ResumoTriagem>(
  'secretariaArbitral/fetchResumoTriagem',
  async () => secretariaArbitralService.obterResumoTriagem(),
);

export const validarLoteProcessos = createAsyncThunk<LoteValidacaoResponse, LoteValidacaoInput>(
  'secretariaArbitral/validarLote',
  async (input) => secretariaArbitralService.validarLote(input),
);

export const MENSAGEM_LOTE_JA_EXECUTADO =
  'Este lote já foi executado. Feche a janela e atualize a lista para ver o resultado.';

export const executarLoteProcessos = createAsyncThunk<
  LoteExecucaoResponse,
  LoteRequestInput,
  { rejectValue: LoteExecucaoErro }
>('secretariaArbitral/executarLote', async (input, { rejectWithValue }) => {
  try {
    return await secretariaArbitralService.executarLote(input);
  } catch (erro: unknown) {
    if (erro instanceof ConflitoIdempotenciaError) {
      return rejectWithValue({
        mensagem: MENSAGEM_LOTE_JA_EXECUTADO,
        conflitoIdempotencia: true,
        loteId: erro.recursoId,
      });
    }
    return rejectWithValue({
      mensagem: getErrorMessage(erro),
      conflitoIdempotencia: false,
      loteId: null,
    });
  }
});

export const EXPORT_MAX_PAGINAS = 50;
const EXPORT_PAGE_LIMIT = 200;

/**
 * Varre a listagem inteira do filtro, página a página, até o teto.
 *
 * Sob demanda e por ação explícita do operador — exportar, ou agir sobre o
 * conjunto inteiro pelo card proativo. É o oposto do que o painel fazia ao
 * ABRIR: lá o laço era custo fixo de entrada; aqui é o trabalho pedido.
 *
 * `truncado` é a verdade sobre o teto e tem de chegar até a UI: passar adiante
 * uma lista silenciosamente cortada é o mesmo defeito da janela de 2.000.
 */
async function coletarTodasAsPaginas(
  filters: ListProcessosFilters,
  maxPaginas: number,
): Promise<ExportarProcessosResultado> {
  const todos: ProcessoArbitralListItem[] = [];
  let pagina = 1;
  let totalPaginas = 1;
  do {
    const resposta = await secretariaArbitralService.listarProcessos({
      ...filters,
      page: pagina,
      limit: EXPORT_PAGE_LIMIT,
    });
    todos.push(...resposta.items);
    totalPaginas = Math.max(1, Math.ceil(resposta.total / resposta.limit));
    pagina += 1;
  } while (pagina <= totalPaginas && pagina <= maxPaginas);
  return { items: todos, truncado: totalPaginas > maxPaginas };
}

export const exportarProcessos = createAsyncThunk<ExportarProcessosResultado, ListProcessosFilters>(
  'secretariaArbitral/exportarProcessos',
  async (filters) => coletarTodasAsPaginas(filters, EXPORT_MAX_PAGINAS),
);

/**
 * Teto de varredura PRÓPRIO do card proativo, separado do da exportação.
 *
 * Exportar é leitura: 10.000 linhas viram um arquivo e o custo morre ali. O card
 * proativo é ESCRITA — cada id vira uma decisão, e o conjunto sai daqui direto
 * para `executarLoteEmLevas`, que o envia em levas de 100. Com o teto da
 * exportação, um clique podia significar 50 GETs seguidos de 100 POSTs, cada um
 * agendando até 100 Cloud Tasks, atrás de um spinner sem progresso nem
 * cancelamento. 10 páginas × 200 = 2.000 elegíveis por rodada, e o aviso de
 * `truncado` (que já existia) diz ao operador que sobrou fila para a próxima.
 *
 * // TODO(web#1070): o teto reduz a exposição, não a resolve — falta progresso
 * por leva e retomada. Retomada não se resolve aqui: os endpoints de lote
 * recebem só os ids da leva, sem `jobId` nem cursor (contrato do core).
 */
const ELEGIVEIS_MAX_PAGINAS = 10;

/**
 * O conjunto INTEIRO de casos numa situação de triagem (REQ-10 / A-03).
 *
 * O card proativo anunciava o número global (vindo do resumo) e agia sobre
 * `itens` — a janela do painel. A variável chamava-se `aTriarGlobal` e não era:
 * o texto afirmava o mundo e o botão operava sobre a memória do componente. Com
 * a fila paginada essa janela cai para 100, então a paginação AGRAVARIA o
 * defeito se o card continuasse lendo dela.
 *
 * Mesmo recorte do resumo (situação, sem outros filtros), para o número do texto
 * e o alcance do botão serem o mesmo conjunto.
 */
export const fetchElegiveisTriagem = createAsyncThunk<
  ExportarProcessosResultado & { situacao: SituacaoTriagemApi },
  { situacao: SituacaoTriagemApi }
>('secretariaArbitral/fetchElegiveisTriagem', async ({ situacao }) => {
  const resultado = await coletarTodasAsPaginas(
    {
      situacaoTriagem: [situacao],
      // O card anuncia o número do RESUMO (que o servidor já filtra pelo recorte da triagem) e
      // age sobre ESTE conjunto: sem o recorte, o texto e o botão falariam de
      // universos diferentes — a classe de defeito do A-03, por outro caminho.
      origem: ORIGEM_TRIAGEM,
      sortBy: 'dataEntrada',
      sortOrder: 'asc',
    },
    ELEGIVEIS_MAX_PAGINAS,
  );
  return { ...resultado, situacao };
});

export const fetchPrazosAbertos = createAsyncThunk<PrazosPainelData, PrazosPainelQuery | undefined>(
  'secretariaArbitral/fetchPrazosAbertos',
  async (query) => secretariaArbitralService.listarPrazosAbertos(query),
);

export const iniciarTriagem = createAsyncThunk<
  { processoId: string; jaEstavaIniciada: boolean },
  { processoId: string },
  { rejectValue: string }
>('secretariaArbitral/iniciarTriagem', async ({ processoId }, { rejectWithValue }) => {
  try {
    await secretariaArbitralService.iniciarTriagem(processoId);
    return { processoId, jaEstavaIniciada: false };
  } catch (error: unknown) {
    if (temCodigoHttp(error, 'TRIAGEM_JA_INICIADA')) {
      return { processoId, jaEstavaIniciada: true };
    }
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao iniciar triagem'));
  }
});

export const fetchChecklistTriagem = createAsyncThunk<
  { processoId: string; checklist: ChecklistTriagem },
  { processoId: string },
  { rejectValue: string }
>('secretariaArbitral/fetchChecklistTriagem', async ({ processoId }, { rejectWithValue }) => {
  try {
    const checklist = await secretariaArbitralService.obterChecklistTriagem(processoId);
    return { processoId, checklist };
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao carregar checklist de triagem'));
  }
});

export const salvarChecklistTriagem = createAsyncThunk<
  { processoId: string },
  {
    processoId: string;
    itens: ChecklistTriagemAvaliacaoInput[];
    origemAvaliacao?: OrigemAvaliacaoChecklist;
  },
  { rejectValue: string }
>(
  'secretariaArbitral/salvarChecklistTriagem',
  async ({ processoId, itens, origemAvaliacao }, { rejectWithValue }) => {
    try {
      await secretariaArbitralService.salvarChecklistTriagem(processoId, itens, origemAvaliacao);
      return { processoId };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao salvar checklist'));
    }
  },
);

export const fetchAuditoriaTriagem = createAsyncThunk<
  { processoId: string; page: AuditoriaTriagemPage; anexar: boolean },
  { processoId: string; cursor?: string },
  { rejectValue: string }
>(
  'secretariaArbitral/fetchAuditoriaTriagem',
  async ({ processoId, cursor }, { rejectWithValue }) => {
    try {
      const page = await secretariaArbitralService.listarAuditoriaTriagem(processoId, cursor);
      return { processoId, page, anexar: Boolean(cursor) };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao carregar a auditoria da triagem'));
    }
  },
);

export const registrarExportacaoTriagem = createAsyncThunk<
  { processoIds: string[] },
  { processoIds: string[]; formato: 'csv' | 'xlsx' },
  { rejectValue: string }
>(
  'secretariaArbitral/registrarExportacaoTriagem',
  async ({ processoIds, formato }, { rejectWithValue }) => {
    const registrados: string[] = [];
    for (const lote of dividirEmLotes(processoIds, LOTE_EXPORTACAO_AUDITORIA)) {
      try {
        await secretariaArbitralService.registrarExportacaoTriagem(lote, formato);
        registrados.push(...lote);
      } catch (error: unknown) {
        const mensagem = getErrorMessage(error, 'Erro ao registrar exportação na auditoria');
        reportError(error instanceof Error ? error : new Error(mensagem), {
          source: 'triagem-exportacao-auditoria',
          formato,
          totalProcessos: processoIds.length,
          registrados: registrados.length,
          naoRegistrados: processoIds.length - registrados.length,
        });
        return rejectWithValue(apresentarMotivoLote(mensagem).texto);
      }
    }
    return { processoIds: registrados };
  },
);

export const fetchDecisaoTriagemAtiva = createAsyncThunk<
  { processoId: string; decisao: TriagemDecisaoAtiva | null },
  { processoId: string },
  { rejectValue: string }
>('secretariaArbitral/fetchDecisaoTriagemAtiva', async ({ processoId }, { rejectWithValue }) => {
  try {
    const decisao = await secretariaArbitralService.obterDecisaoTriagemAtiva(processoId);
    return { processoId, decisao };
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao consultar decisão de triagem'));
  }
});

export const desfazerTriagem = createAsyncThunk<
  { processoId: string },
  { processoId: string },
  { rejectValue: string }
>('secretariaArbitral/desfazerTriagem', async ({ processoId }, { rejectWithValue }) => {
  try {
    await secretariaArbitralService.desfazerTriagem(processoId);
    return { processoId };
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao desfazer decisão'));
  }
});

/**
 * Cap RÍGIDO do lote no core (`triagemIniciarLoteSchema` / `triagemDecidirLoteSchema`:
 * `.max(100)`). Mandar 101 ids não é "um pouco mais lento" — é 400 na validação.
 */
export const LOTE_TRIAGEM_MAX = 100;

/**
 * Executa a ação de lote em LEVAS de `LOTE_TRIAGEM_MAX`, sequencialmente, e junta
 * as partições devolvidas.
 *
 * A spec da triagem em lista sempre disse que "ação no conjunto inteiro segue
 * pelos cards proativos globais", mas o particionamento que ela pede nunca foi
 * escrito: o card mandava `ids.map(...)` cru e, acima de 100, a chamada morria na
 * validação. Sequencial (e não `Promise.all`) porque cada leva agenda até 100
 * Cloud Tasks no core; disparar 21 levas juntas é um pico desnecessário.
 *
 * A falha de UMA leva não descarta as anteriores: as decisões já agendadas foram
 * agendadas de verdade, e sumir com elas do resultado tiraria do operador o
 * botão "Desfazer" sobre processos que de fato mudaram.
 *
 * Mas o laço PARA quando a falha não é da leva e sim do canal: sessão expirada
 * (401/403) ou servidor fora (5xx) não melhoram na leva seguinte, e insistir
 * significaria disparar dezenas de POSTs contra quem já respondeu erro, com o
 * operador esperando o ciclo inteiro para ouvir "nada passou". As levas que
 * sobraram entram como falha com o mesmo motivo, SEM serem enviadas — o
 * resultado continua descrevendo o conjunto inteiro.
 */
function interrompeOLote(erro: unknown): boolean {
  // Sem `status` não houve resposta: `TypeError: Failed to fetch` de rede caída é
  // o caso de canal MAIS comum, e a primeira versão desta função o classificava
  // como falha da leva — o laço seguia disparando todas as levas restantes
  // contra uma conexão que já tinha morrido.
  if (!ehErroHttp(erro)) return true;
  return erro.status === 401 || erro.status === 403 || erro.status >= 500;
}

// TODO(core#1667): sem chave de idempotência no contrato do lote, uma falha de
// transporte DEPOIS de o servidor processar a leva depende do guard por estado do
// core (`triarLote.ts:55`) para não reaplicar. Confirmar que o `decidirLote` tem
// o equivalente.
async function executarLoteEmLevas(
  processoIds: string[],
  executar: (leva: string[]) => Promise<ResultadoLoteTriagem>,
): Promise<ResultadoLoteTriagem> {
  const acumulado: ResultadoLoteTriagem = { processados: [], agendadas: [], falhas: [] };
  const levas = dividirEmLotes(processoIds, LOTE_TRIAGEM_MAX);
  for (const [indice, leva] of levas.entries()) {
    try {
      const parcial = await executar(leva);
      acumulado.processados.push(...parcial.processados);
      acumulado.agendadas.push(...parcial.agendadas);
      acumulado.falhas.push(...parcial.falhas);
    } catch (error: unknown) {
      const motivo = motivoAcaoLegivel(error, 'A leva falhou');
      const abortar = interrompeOLote(error);
      const naoEnviadas = abortar ? levas.slice(indice) : [leva];
      for (const pendente of naoEnviadas) {
        acumulado.falhas.push(
          ...pendente.map((processoArbitralId) => ({ processoArbitralId, motivo })),
        );
      }
      if (abortar) break;
    }
  }
  return acumulado;
}

export const iniciarTriagemLote = createAsyncThunk<
  ResultadoLoteTriagem,
  { processoIds: string[] },
  { rejectValue: string }
>('secretariaArbitral/iniciarTriagemLote', async ({ processoIds }, { rejectWithValue }) => {
  const resultado = await executarLoteEmLevas(processoIds, (leva) =>
    secretariaArbitralService.iniciarTriagemLote(leva),
  );
  // Só é rejeição quando NADA passou — com resultado parcial o operador precisa
  // ver o que entrou (e poder desfazer), não um toast de erro genérico.
  if (resultado.processados.length === 0 && resultado.falhas.length > 0) {
    return rejectWithValue(resultado.falhas[0]?.motivo ?? 'Erro ao iniciar triagem em lote');
  }
  return resultado;
});

export const decidirTriagemLote = createAsyncThunk<
  ResultadoLoteTriagem,
  { processoIds: string[]; decisao: TriagemDecisaoLote },
  { rejectValue: string }
>(
  'secretariaArbitral/decidirTriagemLote',
  async ({ processoIds, decisao }, { rejectWithValue }) => {
    const resultado = await executarLoteEmLevas(processoIds, (leva) =>
      secretariaArbitralService.decidirTriagemLote(leva, decisao),
    );
    if (resultado.processados.length === 0 && resultado.falhas.length > 0) {
      return rejectWithValue(resultado.falhas[0]?.motivo ?? 'Erro ao decidir triagem em lote');
    }
    return resultado;
  },
);

export const decidirTriagem = createAsyncThunk<
  { processoId: string; agendada: TriagemDecisaoAgendada },
  { processoId: string; decisao: TriagemDecisao },
  { rejectValue: string }
>('secretariaArbitral/decidirTriagem', async ({ processoId, decisao }, { rejectWithValue }) => {
  try {
    const agendada = await secretariaArbitralService.decidirTriagem(processoId, decisao);
    return { processoId, agendada };
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao decidir triagem'));
  }
});

export const registrarConcluso = createAsyncThunk<
  ConclusoResult & { processoId: string },
  { processoId: string; input: ConclusoInput }
>('secretariaArbitral/registrarConcluso', async ({ processoId, input }) => {
  const result = await secretariaArbitralService.registrarConcluso(processoId, input);
  return { ...result, processoId };
});

export const sugerirSorteio = createAsyncThunk<
  { processoId: string; sugestao: SorteioSugestao },
  { processoId: string; body: SorteioBody }
>('secretariaArbitral/sugerirSorteio', async ({ processoId, body }) => {
  const sugestao = await secretariaArbitralService.sugerirSorteio(processoId, body);
  return { processoId, sugestao };
});

export const confirmarSorteio = createAsyncThunk<
  SorteioResult & { processoId: string },
  { processoId: string; body: SorteioBody }
>('secretariaArbitral/confirmarSorteio', async ({ processoId, body }) => {
  const result = await secretariaArbitralService.confirmarSorteio(processoId, body);
  return { ...result, processoId };
});

export const registrarManifestacao = createAsyncThunk<
  { processoId: string; manifestacaoId: string },
  { processoId: string; manifestacaoId: string; descricaoAndamento?: string },
  { rejectValue: string }
>(
  'secretariaArbitral/registrarManifestacao',
  async ({ processoId, manifestacaoId, descricaoAndamento }, { dispatch, rejectWithValue }) => {
    try {
      await secretariaArbitralService.registrarManifestacao(
        processoId,
        manifestacaoId,
        descricaoAndamento,
      );
      await dispatch(fetchManifestacoesProcesso({ processoId }));
      return { processoId, manifestacaoId };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const desconsiderarManifestacao = createAsyncThunk<
  { processoId: string; manifestacaoId: string; justificativa: string },
  { processoId: string; manifestacaoId: string; justificativa: string },
  { rejectValue: string }
>(
  'secretariaArbitral/desconsiderarManifestacao',
  async ({ processoId, manifestacaoId, justificativa }, { dispatch, rejectWithValue }) => {
    try {
      await secretariaArbitralService.desconsiderarManifestacao(
        processoId,
        manifestacaoId,
        justificativa,
      );
      await dispatch(fetchManifestacoesProcesso({ processoId }));
      return { processoId, manifestacaoId, justificativa };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const criarManifestacao = createAsyncThunk<
  { processoId: string; manifestacao: ManifestacaoListItem },
  { processoId: string; input: CriarManifestacaoInput },
  { rejectValue: string }
>(
  'secretariaArbitral/criarManifestacao',
  async ({ processoId, input }, { dispatch, rejectWithValue }) => {
    const diasRetroativos = diasDeRetroatividade(input.diaRecebimento, hojeEmSaoPaulo());
    try {
      const manifestacao = await secretariaArbitralService.criarManifestacao(processoId, input);
      datadogRum.addAction('manifestacao_registrada_manual', {
        processoArbitralId: processoId,
        canal: input.canal,
        comAnexo: input.arquivos.length > 0,
        sigilo: input.sigilo,
        diasRetroativos,
      });
      await dispatch(fetchManifestacoesProcesso({ processoId }));
      return { processoId, manifestacao };
    } catch (error: unknown) {
      const mensagem = getErrorMessage(error);
      reportError(error instanceof Error ? error : new Error(mensagem), {
        source: 'manifestacao-registro-manual',
        processoArbitralId: processoId,
        canal: input.canal,
        diasRetroativos,
        statusHttp: ehErroHttp(error) ? error.status : null,
      });
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const fetchElegibilidadeTransito = createAsyncThunk<
  { processoId: string; elegibilidade: TransitoElegibilidade },
  string,
  { rejectValue: string }
>('secretariaArbitral/fetchElegibilidadeTransito', async (processoId, { rejectWithValue }) => {
  try {
    const elegibilidade = await secretariaArbitralService.obterElegibilidadeTransito(processoId);
    return { processoId, elegibilidade };
  } catch (error: unknown) {
    // `rejectWithValue` como os vizinhos: sem ele o RTK serializa a exceção
    // (`miniSerializeError`) e `unwrap()` rejeita com um OBJETO PLANO — o `instanceof Error` do
    // componente nunca é verdade, e a mensagem do back (inclusive o 404 de "back ainda não
    // deployado") era trocada por um texto genérico (COR-10 da review do #971).
    return rejectWithValue(getErrorMessage(error));
  }
});

export const publicarTransito = createAsyncThunk<
  PublicarTransitoResult,
  { processoId: string; body: PublicarTransitoBody }
>('secretariaArbitral/publicarTransito', async ({ processoId, body }) => {
  return secretariaArbitralService.publicarTransito(processoId, body);
});

export const fetchDetalheProcesso = createAsyncThunk<ProcessoDetalhe, string>(
  'secretariaArbitral/fetchDetalheProcesso',
  async (processoId) => secretariaArbitralService.obterDetalheProcesso(processoId),
);

export const reabrirProcesso = createAsyncThunk<
  ReabrirProcessoResult,
  { processoId: string; body: ReabrirProcessoBody },
  { rejectValue: string }
>('secretariaArbitral/reabrirProcesso', async ({ processoId, body }, { rejectWithValue }) => {
  try {
    return await secretariaArbitralService.reabrirProcesso(processoId, body);
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error));
  }
});

export const liberarSentenca = createAsyncThunk<
  LiberarSentencaResult,
  { processoId: string; body: LiberarSentencaBody },
  { rejectValue: string }
>('secretariaArbitral/liberarSentenca', async ({ processoId, body }, { rejectWithValue }) => {
  let liberacao: LiberarSentencaResult;
  try {
    liberacao = await secretariaArbitralService.liberarSentenca(processoId, body);
  } catch (error: unknown) {
    // 4xx (409 já liberada, 400 de status ou sem prazo, 403) é recusa de negócio que o diálogo
    // mostra: vira ação contável. Só 5xx e falha de rede viram erro, para não alarmar à toa.
    if (ehErroHttp(error) && error.status < 500) {
      datadogRum.addAction('sentenca_trava_liberacao_recusada', {
        processoArbitralId: processoId,
        motivo: body.motivo,
        statusHttp: error.status,
        codigo: error.codigo,
      });
    } else {
      const mensagem = getErrorMessage(error);
      reportError(error instanceof Error ? error : new Error(mensagem), {
        source: 'sentenca-liberacao',
        processoArbitralId: processoId,
        motivo: body.motivo,
        statusHttp: ehErroHttp(error) ? error.status : null,
      });
    }
    return rejectWithValue(motivoAcaoLegivel(error));
  }
  // Fora do `try`: a liberação já gravou no core, e um 2xx sem `prazosCobertos` não pode virar
  // recusa no diálogo só porque a contagem da telemetria leu `.length` de `undefined`.
  // A justificativa é texto livre sobre o caso (LGPD): a telemetria leva só o enum e contagens.
  datadogRum.addAction('sentenca_trava_liberada', {
    processoArbitralId: processoId,
    motivo: body.motivo,
    prazosCobertos: Array.isArray(liberacao.prazosCobertos) ? liberacao.prazosCobertos.length : 0,
    comAnexo: (body.anexoUploadIds?.length ?? 0) > 0,
    comAndamentoVinculado: Boolean(body.andamentoVinculadoId),
  });
  return liberacao;
});

export const fetchManifestacoesProcesso = createAsyncThunk<
  { processoId: string; items: ManifestacaoListItem[] },
  { processoId: string; somenteRegistradas?: boolean; limit?: number }
>(
  'secretariaArbitral/fetchManifestacoesProcesso',
  async ({ processoId, somenteRegistradas, limit }) => {
    const items = await secretariaArbitralService.listarManifestacoes(processoId, {
      somenteRegistradas,
      limit,
    });
    return { processoId, items };
  },
);

export const fetchPendenciasProcesso = createAsyncThunk<
  { processoId: string; resultado: ListarPendenciasSecretariaResult },
  { processoId: string } & ListarPendenciasFilters
>('secretariaArbitral/fetchPendenciasProcesso', async ({ processoId, ...filtros }) => {
  const resultado = await secretariaArbitralService.listarPendenciasDoProcesso(processoId, filtros);
  return { processoId, resultado };
});

/**
 * A lista E a badge da aba: as duas mudam a cada ato sobre pendências, e vêm de endpoints
 * diferentes. A badge sai de `detalhe.counts`, não da lista — sem a segunda releitura ela fica
 * com o número de antes do ato até alguém recarregar a página.
 *
 * O filtro é lido do estado NA HORA de reler, não recebido pronto: entre o clique e a volta do
 * POST a pessoa pode ter trocado de filtro, e a releitura com o valor antigo devolveria a lista
 * de um filtro sob o chip de outro.
 */
function releituraDePendencias(
  dispatch: (acao: unknown) => unknown,
  getState: () => RootState,
  processoId: string,
): Promise<unknown> {
  const filtro = selectFiltroPendencias(processoId)(getState());
  return Promise.all([
    dispatch(fetchPendenciasProcesso({ processoId, filtro })),
    dispatch(fetchDetalheProcesso(processoId)),
  ]);
}

/**
 * Cria e **relê**: a resposta traz só os ids do ato, e o card depende de urgência, rastro e
 * destinatário resolvido, que o backend deriva. Atualizar a lista por otimismo mostraria um
 * card montado pela tela em vez do que ficou gravado.
 *
 * No erro relê **só o detalhe**: o 409 `PROCESSO_ENCERRADO` (C27) chega de uma tela aberta antes
 * do encerramento, e é o detalhe que traz o status e tira os botões de criar. A lista fica de
 * fora porque a criação recusada não a mudou, e relê-la apagava o composer preenchido nas outras
 * recusas (400, 5xx, rede): com a lista vazia o painel troca a aba pelo skeleton enquanto a busca
 * corre, e com a busca falhando troca pelo card de erro (review do PR web#1447, COR-01).
 */
export const criarPendenciaProcesso = createAsyncThunk<
  CriarPendenciaResultado,
  { processoId: string; payload: CriarPendenciaPayload },
  { state: RootState; rejectValue: string }
>(
  'secretariaArbitral/criarPendenciaProcesso',
  async ({ processoId, payload }, { dispatch, getState, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.criarPendenciaNoProcesso(
        processoId,
        payload,
      );
      await releituraDePendencias(dispatch, getState, processoId);
      return resultado;
    } catch (error: unknown) {
      await dispatch(fetchDetalheProcesso(processoId));
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

/**
 * Movimenta e relê. Sem update otimista **por causa do resultado parcial**: o ato pode
 * resolver 3 de 5 pendências, e uma tela que antecipasse as 5 mostraria como resolvido o que
 * o backend recusou por conflito, tipo ou status.
 *
 * Relê **também no erro**, e não é zelo: quando NENHUMA pendência se move o backend responde
 * 409, não 200 com resultado parcial. E o 409 mais comum é justamente o de estado obsoleto —
 * alguém movimentou pela outra aba. Sem esta releitura o card recusado continua aberto na
 * tela, convidando ao mesmo clique de novo.
 */
export const movimentarPendencias = createAsyncThunk<
  MovimentarPendenciasResultado,
  { payload: MovimentarPendenciasPayload },
  { state: RootState; rejectValue: string }
>(
  'secretariaArbitral/movimentarPendencias',
  async ({ payload }, { dispatch, getState, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.movimentarPendencias(payload);
      await releituraDePendencias(dispatch, getState, payload.processoArbitralId);
      return resultado;
    } catch (error: unknown) {
      await releituraDePendencias(dispatch, getState, payload.processoArbitralId);
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const previewPrazoPendencia = createAsyncThunk<
  PrazoPendenciaPreview,
  number,
  { rejectValue: string }
>('secretariaArbitral/previewPrazoPendencia', async (diasUteis, { rejectWithValue }) => {
  try {
    return await secretariaArbitralService.previewPrazoPendencia(diasUteis);
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error));
  }
});

export const validarPendencia = createAsyncThunk<
  ValidarPendenciaResult,
  {
    processoId: string;
    pendenciaId: string;
    acao: ValidacaoPendenciaAcao;
    motivo?: string;
    prazo?: PrazoDaRecusa;
  }
>('secretariaArbitral/validarPendencia', async ({ processoId, pendenciaId, acao, motivo, prazo }) =>
  secretariaArbitralService.validarPendencia(processoId, pendenciaId, { acao, motivo, ...prazo }),
);

export const publicarDecurso = createAsyncThunk<
  PublicarDecursoResult,
  { processoId: string; body?: PublicarDecursoBody }
>('secretariaArbitral/publicarDecurso', async ({ processoId, body }) =>
  secretariaArbitralService.publicarDecurso(processoId, body ?? {}),
);

export const fetchRankingArbitros = createAsyncThunk<RankingArbitroItem[]>(
  'secretariaArbitral/fetchRankingArbitros',
  async () => secretariaArbitralService.listarRankingArbitros(),
);

export const substituirRankingArbitros = createAsyncThunk<
  RankingArbitroItem[],
  RankingArbitroItem[]
>('secretariaArbitral/substituirRankingArbitros', async (itens) =>
  secretariaArbitralService.substituirRankingArbitros(itens),
);

export const fetchArList = createAsyncThunk<ArListResult, ArListFiltros | undefined>(
  'secretariaArbitral/fetchArList',
  async (arg) => secretariaArbitralService.listarAr(arg ?? {}),
);

export const fetchArDetalhe = createAsyncThunk<ArDetalhe, string>(
  'secretariaArbitral/fetchArDetalhe',
  async (envioFisicoId) => secretariaArbitralService.obterArDetalhe(envioFisicoId),
);

export const fetchArAEnviar = createAsyncThunk<ArAEnviarResult, ArAEnviarFiltros | undefined>(
  'secretariaArbitral/fetchArAEnviar',
  async (filtros) => secretariaArbitralService.listarArAEnviar(filtros ?? {}),
);

export const fetchRequeridoAEnviar = createAsyncThunk<RequeridoAEnviarDetalhe, string>(
  'secretariaArbitral/fetchRequeridoAEnviar',
  async (parteArbitralId) => secretariaArbitralService.obterRequeridoAEnviar(parteArbitralId),
);

/**
 * Cota o envio para um CEP.
 *
 * O `requestId` do thunk é o que permite ao reducer descartar a resposta de uma
 * cotação antiga — a secretaria digita o CEP e troca a modalidade rápido, e sem o
 * descarte um valor velho sobrescreve o novo na tela.
 */
/**
 * Falha da cotação, com o CÓDIGO preservado.
 *
 * `createAsyncThunk` serializa o erro e descarta campos próprios, então sem
 * `rejectWithValue` o `codigo` do `ErroHttp` some — e a tela perde como
 * distinguir "a transportadora não atende este CEP" (insistir não resolve) de
 * "não deu para cotar agora" (tentar de novo resolve).
 */
export interface CotarEnvioArFalha {
  codigo: string | null;
  mensagem: string;
}

export const cotarEnvioAr = createAsyncThunk<
  CotacaoEnvioAr,
  string,
  { rejectValue: CotarEnvioArFalha }
>('secretariaArbitral/cotarEnvioAr', async (cep, { rejectWithValue }) => {
  try {
    return await secretariaArbitralService.cotarEnvioAr(cep);
  } catch (erro) {
    if (ehErroHttp(erro)) {
      return rejectWithValue({ codigo: erro.codigo, mensagem: erro.message });
    }
    // Sem resposta HTTP: o `fetch` rejeitou (rede caída, servidor fora, CORS). O
    // `erro.message` aqui é texto do NAVEGADOR — "Failed to fetch" — e não pode
    // chegar à tela. É indisponibilidade nossa, e o código transitório é o que faz
    // o drawer oferecer "Tentar de novo" em vez de mandar corrigir o CEP.
    return rejectWithValue({
      codigo: CODIGOS_SOLICITACAO_AR.REDE_INDISPONIVEL,
      mensagem: 'Não foi possível calcular o valor do envio.',
    });
  }
});

/**
 * Falha do confirm, com a CAUSA preservada.
 *
 * `createAsyncThunk` serializa o erro em `{ name, message, stack }` e descarta
 * campos próprios — o `codigo` do `ErroHttp` e a cotação nova do
 * `CotacaoDefasadaError` morreriam no caminho. Sem `rejectWithValue`, a tela
 * teria de adivinhar a causa pelo texto da mensagem, que muda com a copy.
 */
export interface SolicitarEnvioArFalha {
  codigo: string | null;
  mensagem: string;
  cotacaoAtual: CotacaoEnvioAr | null;
}

export const solicitarEnvioAr = createAsyncThunk<
  { parteArbitralId: string; resultado: SolicitarEnvioArResultado },
  SolicitarEnvioArPayload,
  { rejectValue: SolicitarEnvioArFalha }
>('secretariaArbitral/solicitarEnvioAr', async (payload, { rejectWithValue }) => {
  try {
    const resultado = await secretariaArbitralService.solicitarEnvioAr(payload);
    return { parteArbitralId: payload.parteArbitralId, resultado };
  } catch (erro) {
    if (erro instanceof CotacaoDefasadaError) {
      return rejectWithValue({
        codigo: CODIGOS_SOLICITACAO_AR.COTACAO_DEFASADA,
        mensagem: erro.message,
        cotacaoAtual: erro.cotacaoAtual,
      });
    }
    if (ehErroHttp(erro)) {
      return rejectWithValue({ codigo: erro.codigo, mensagem: erro.message, cotacaoAtual: null });
    }
    // Mesmo caso do `cotarEnvioAr`: sem resposta HTTP, a mensagem do navegador não
    // serve de copy. E aqui o recado precisa ser explícito de que NADA foi criado
    // — a secretaria tenta de novo com o mesmo `clientRequestId`, que o servidor
    // reconhece como replay caso a requisição tenha chegado sem a resposta voltar.
    return rejectWithValue({
      codigo: CODIGOS_SOLICITACAO_AR.REDE_INDISPONIVEL,
      mensagem: 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.',
      cotacaoAtual: null,
    });
  }
});

/**
 * Falha da reemissão, com o CÓDIGO preservado — mesmo motivo do `solicitarEnvioAr`:
 * `createAsyncThunk` serializa o erro e descarta o `codigo` do `ErroHttp`, e é por
 * ele que a tela distingue "essa cobrança não é reemitível" (409, nada a fazer aqui)
 * de "tentou demais" (429) ou de uma falha qualquer.
 */
export interface ReemitirCobrancaArFalha {
  codigo: string | null;
  mensagem: string;
}

export const reemitirCobrancaAr = createAsyncThunk<
  ReemitirCobrancaArResultado,
  string,
  { rejectValue: ReemitirCobrancaArFalha }
>('secretariaArbitral/reemitirCobrancaAr', async (solicitacaoId, { rejectWithValue }) => {
  try {
    return await secretariaArbitralService.reemitirCobrancaAr(solicitacaoId);
  } catch (erro) {
    if (ehErroHttp(erro)) {
      return rejectWithValue({ codigo: erro.codigo, mensagem: erro.message });
    }
    return rejectWithValue({
      codigo: CODIGOS_SOLICITACAO_AR.REDE_INDISPONIVEL,
      mensagem: 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.',
    });
  }
});

export const fetchArDoProcesso = createAsyncThunk<
  { processoId: string; items: ArListItem[] },
  string
>('secretariaArbitral/fetchArDoProcesso', async (processoId) => {
  const items = await secretariaArbitralService.listarArDoProcesso(processoId);
  return { processoId, items };
});

export const fetchPartesProcesso = createAsyncThunk<
  { processoId: string; items: ParteCompletaItem[] },
  string
>('secretariaArbitral/fetchPartesProcesso', async (processoId) => {
  const items = await secretariaArbitralService.listarPartesCompletas(processoId);
  return { processoId, items };
});

export const consultarDocumentoDaParte = createAsyncThunk<
  ConsultaDocumentoParteResultado,
  string,
  { rejectValue: string }
>('secretariaArbitral/consultarDocumentoDaParte', async (documento, { rejectWithValue }) => {
  try {
    return await secretariaArbitralService.consultarDocumentoDaParte(documento);
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error));
  }
});

export const criarParteProcesso = createAsyncThunk<
  { processoId: string; resultado: CriarParteArbitralResultado },
  { processoId: string; input: CriarParteArbitralInput },
  { rejectValue: string }
>(
  'secretariaArbitral/criarParteProcesso',
  async ({ processoId, input }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.criarParte(processoId, input);
      await dispatch(fetchPartesProcesso(processoId));
      return { processoId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const editarParteProcesso = createAsyncThunk<
  { processoId: string; resultado: EditarParteArbitralResultado },
  { processoId: string; parteArbitralId: string; input: EditarParteArbitralInput },
  { rejectValue: string }
>(
  'secretariaArbitral/editarParteProcesso',
  async ({ processoId, parteArbitralId, input }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.editarParte(
        processoId,
        parteArbitralId,
        input,
      );
      await dispatch(fetchPartesProcesso(processoId));
      return { processoId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const fetchAdvogadosDaParte = createAsyncThunk<
  { parteArbitralId: string; items: AdvogadoDaParteItem[] },
  { processoId: string; parteArbitralId: string },
  { rejectValue: string }
>(
  'secretariaArbitral/fetchAdvogadosDaParte',
  async ({ processoId, parteArbitralId }, { rejectWithValue }) => {
    try {
      const items = await secretariaArbitralService.listarAdvogadosDaParte(
        processoId,
        parteArbitralId,
      );
      return { parteArbitralId, items };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const adicionarAdvogadoDaParte = createAsyncThunk<
  { parteArbitralId: string; resultado: AlterarAdvogadoResultado },
  { processoId: string; parteArbitralId: string; input: AdicionarAdvogadoParteInput },
  { rejectValue: string }
>(
  'secretariaArbitral/adicionarAdvogadoDaParte',
  async ({ processoId, parteArbitralId, input }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.adicionarAdvogadoDaParte(
        processoId,
        parteArbitralId,
        input,
      );
      await dispatch(fetchAdvogadosDaParte({ processoId, parteArbitralId }));
      return { parteArbitralId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const editarAdvogadoDaParte = createAsyncThunk<
  { parteArbitralId: string; resultado: AlterarAdvogadoResultado },
  {
    processoId: string;
    parteArbitralId: string;
    advogadoParteArbitralId: string;
    input: EditarAdvogadoParteInput;
  },
  { rejectValue: string }
>(
  'secretariaArbitral/editarAdvogadoDaParte',
  async (
    { processoId, parteArbitralId, advogadoParteArbitralId, input },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const resultado = await secretariaArbitralService.editarAdvogadoDaParte(
        processoId,
        parteArbitralId,
        advogadoParteArbitralId,
        input,
      );
      await dispatch(fetchAdvogadosDaParte({ processoId, parteArbitralId }));
      return { parteArbitralId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const removerAdvogadoDaParte = createAsyncThunk<
  { parteArbitralId: string; resultado: RemoverAdvogadoResultado },
  {
    processoId: string;
    parteArbitralId: string;
    advogadoParteArbitralId: string;
    motivo: string;
  },
  { rejectValue: string }
>(
  'secretariaArbitral/removerAdvogadoDaParte',
  async (
    { processoId, parteArbitralId, advogadoParteArbitralId, motivo },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const resultado = await secretariaArbitralService.removerAdvogadoDaParte(
        processoId,
        parteArbitralId,
        advogadoParteArbitralId,
        motivo,
      );
      await dispatch(fetchAdvogadosDaParte({ processoId, parteArbitralId }));
      return { parteArbitralId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const desativarParteProcesso = createAsyncThunk<
  { processoId: string; resultado: AlterarSituacaoParteResultado },
  { processoId: string; parteArbitralId: string; motivo: string },
  { rejectValue: string }
>(
  'secretariaArbitral/desativarParteProcesso',
  async ({ processoId, parteArbitralId, motivo }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.desativarParte(
        processoId,
        parteArbitralId,
        motivo,
      );
      await dispatch(fetchPartesProcesso(processoId));
      return { processoId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const reativarParteProcesso = createAsyncThunk<
  { processoId: string; resultado: AlterarSituacaoParteResultado },
  { processoId: string; parteArbitralId: string; motivo: string },
  { rejectValue: string }
>(
  'secretariaArbitral/reativarParteProcesso',
  async ({ processoId, parteArbitralId, motivo }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.reativarParte(
        processoId,
        parteArbitralId,
        motivo,
      );
      await dispatch(fetchPartesProcesso(processoId));
      return { processoId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const removerParteProcesso = createAsyncThunk<
  { processoId: string; resultado: AlterarSituacaoParteResultado },
  { processoId: string; parteArbitralId: string; motivo: string },
  { rejectValue: string }
>(
  'secretariaArbitral/removerParteProcesso',
  async ({ processoId, parteArbitralId, motivo }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.removerParte(
        processoId,
        parteArbitralId,
        motivo,
      );
      await dispatch(fetchPartesProcesso(processoId));
      return { processoId, resultado };
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const reenviarLinkAcessoParte = createAsyncThunk<
  ReenviarLinkAcessoResultado,
  { processoId: string; parteArbitralId: string; canal: CanalAcessoParte; motivo: string },
  { rejectValue: string }
>(
  'secretariaArbitral/reenviarLinkAcessoParte',
  async ({ processoId, parteArbitralId, canal, motivo }, { rejectWithValue }) => {
    try {
      return await secretariaArbitralService.reenviarLinkAcessoParte(processoId, parteArbitralId, {
        canal,
        motivo,
      });
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const solicitarVerificacaoFacialParte = createAsyncThunk<
  VerificacaoFacialParteResultado,
  { processoId: string; parteArbitralId: string; input: VerificacaoFacialParteInput },
  { rejectValue: string }
>(
  'secretariaArbitral/solicitarVerificacaoFacialParte',
  async ({ processoId, parteArbitralId, input }, { rejectWithValue }) => {
    try {
      return await secretariaArbitralService.solicitarVerificacaoFacialParte(
        processoId,
        parteArbitralId,
        input,
      );
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const fetchDocumentosProcesso = createAsyncThunk<
  { processoId: string; items: DocumentoArbitralItem[] },
  string
>('secretariaArbitral/fetchDocumentosProcesso', async (processoId) => {
  const items = await secretariaArbitralService.listarDocumentosProcesso(processoId);
  return { processoId, items };
});

export const fetchSolicitacoesProcesso = createAsyncThunk<
  { processoId: string; filtro: FiltroSolicitacoes; resposta: SolicitacoesSecretariaResposta },
  { processoId: string; filtro: FiltroSolicitacoes }
>('secretariaArbitral/fetchSolicitacoesProcesso', async ({ processoId, filtro }) => {
  const resposta = await secretariaArbitralService.listarSolicitacoesProcesso(processoId, filtro);
  return { processoId, filtro, resposta };
});

/**
 * Decidir mexe em TRÊS listas, não em uma: a solicitação sai de Pendentes, o backend resolve
 * junto a pendência de aviso que ela abriu para a Secretaria, e a badge de cada aba vem do
 * detalhe. Reler só as solicitações deixava a aba Pendências com o card da gêmea já resolvida
 * até alguém recarregar a página — `releituraDePendencias` cobre a lista e o detalhe de uma vez.
 */
export const decidirSolicitacaoProcesso = createAsyncThunk<
  DecidirSolicitacaoResultado,
  DecidirSolicitacaoInput & { filtro: FiltroSolicitacoes },
  { state: RootState; rejectValue: string }
>(
  'secretariaArbitral/decidirSolicitacaoProcesso',
  async ({ filtro, ...input }, { dispatch, getState, rejectWithValue }) => {
    const relerTudo = () =>
      Promise.all([
        dispatch(fetchSolicitacoesProcesso({ processoId: input.processoId, filtro })),
        releituraDePendencias(dispatch, getState, input.processoId),
      ]);
    try {
      const resultado = await secretariaArbitralService.decidirSolicitacao(input);
      await relerTudo();
      return resultado;
    } catch (error: unknown) {
      if (error instanceof ConflitoIdempotenciaError) await relerTudo();
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const concluirSolicitacaoProcesso = createAsyncThunk<
  ConcluirSolicitacaoResultado,
  ConcluirSolicitacaoInput & { filtro: FiltroSolicitacoes },
  { rejectValue: string }
>(
  'secretariaArbitral/concluirSolicitacaoProcesso',
  async ({ filtro, ...input }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await secretariaArbitralService.concluirSolicitacao(input);
      await Promise.all([
        dispatch(fetchSolicitacoesProcesso({ processoId: input.processoId, filtro })),
        dispatch(fetchDetalheProcesso(input.processoId)),
      ]);
      return resultado;
    } catch (error: unknown) {
      if (error instanceof ConflitoIdempotenciaError) {
        await Promise.all([
          dispatch(fetchSolicitacoesProcesso({ processoId: input.processoId, filtro })),
          dispatch(fetchDetalheProcesso(input.processoId)),
        ]);
      }
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const fetchAndamentosProcesso = createAsyncThunk<
  { processoId: string; items: AndamentoArbitralItem[] },
  string
>('secretariaArbitral/fetchAndamentosProcesso', async (processoId) => {
  const items = await secretariaArbitralService.listarAndamentosProcesso(processoId);
  return { processoId, items };
});

export const publicarAndamentoProcesso = createAsyncThunk<
  PublicarAndamentoResultado,
  PublicarAndamentoInput,
  { rejectValue: { mensagem: string; andamentoId: string | null; conflito: boolean } }
>('secretariaArbitral/publicarAndamentoProcesso', async (input, { dispatch, rejectWithValue }) => {
  try {
    const resultado = await secretariaArbitralService.publicarAndamentoProcesso(input);
    await Promise.all([
      dispatch(fetchAndamentosProcesso(input.processoId)),
      dispatch(fetchDetalheProcesso(input.processoId)),
    ]);
    return resultado;
  } catch (error: unknown) {
    const telemetria = {
      source: 'arbitral-publicar-andamento',
      processoId: input.processoId,
      qtdAnexos: input.arquivos.length,
      criarPendencia: input.criarPendencia,
    };
    if (error instanceof ConflitoIdempotenciaError) {
      reportError(error, { ...telemetria, conflito: true });
      await Promise.all([
        dispatch(fetchAndamentosProcesso(input.processoId)),
        dispatch(fetchDetalheProcesso(input.processoId)),
      ]);
      return rejectWithValue({
        mensagem: error.message,
        andamentoId: error.recursoId,
        conflito: true,
      });
    }
    const mensagem = getErrorMessage(error);
    reportError(error instanceof Error ? error : new Error(mensagem), {
      ...telemetria,
      conflito: false,
    });
    await Promise.all([
      dispatch(fetchAndamentosProcesso(input.processoId)),
      dispatch(fetchDetalheProcesso(input.processoId)),
    ]);
    return rejectWithValue({
      mensagem: motivoAcaoLegivel(error),
      andamentoId: null,
      conflito: false,
    });
  }
});

export const carregarTextoModeloAndamento = createAsyncThunk<
  TextoModeloMaterializado,
  { processoId: string; modeloId: string; prazoDiasUteis?: number }
>(
  'secretariaArbitral/carregarTextoModeloAndamento',
  async ({ processoId, modeloId, prazoDiasUteis }) =>
    secretariaArbitralService.obterTextoModeloAndamento(processoId, modeloId, prazoDiasUteis),
);

export const fetchDiasNaoUteis = createAsyncThunk<DiasNaoUteisDoAno, number>(
  'secretariaArbitral/fetchDiasNaoUteis',
  async (ano) => secretariaArbitralService.listarDiasNaoUteis(ano),
);

export const fetchModelosAndamentoOpcoes = createAsyncThunk<ModeloAndamentoOpcao[], void>(
  'secretariaArbitral/fetchModelosAndamentoOpcoes',
  async () => secretariaArbitralService.listarModelosAndamentoOpcoes(),
);

export const enviarAnexoDoProcesso = createAsyncThunk<
  AnexoUploadEnviado,
  { processoId: string; arquivo: File },
  { rejectValue: string }
>(
  'secretariaArbitral/enviarAnexoDoProcesso',
  async ({ processoId, arquivo }, { rejectWithValue }) => {
    try {
      return await secretariaArbitralService.enviarAnexoDoProcesso(processoId, arquivo);
    } catch (error: unknown) {
      return rejectWithValue(motivoAcaoLegivel(error));
    }
  },
);

export const fetchCertificadosProcesso = createAsyncThunk<
  { processoId: string; items: CertificadoArbitralItem[] },
  string
>('secretariaArbitral/fetchCertificadosProcesso', async (processoId) => {
  const items = await secretariaArbitralService.listarCertificadosProcesso(processoId);
  return { processoId, items };
});

export const fetchUrlCertificadoDownload = createAsyncThunk<
  { url: string },
  { processoId: string; certificadoId: string }
>('secretariaArbitral/fetchUrlCertificadoDownload', async ({ processoId, certificadoId }) => ({
  url: await secretariaArbitralService.obterUrlCertificadoDownload(processoId, certificadoId),
}));

export const fetchCertificadoAcessosProcesso = createAsyncThunk<
  { processoId: string; certificado: CertificadoAcessosArbitral },
  string
>('secretariaArbitral/fetchCertificadoAcessosProcesso', async (processoId) => ({
  processoId,
  certificado: await secretariaArbitralService.obterCertificadoAcessosProcesso(processoId),
}));

export const fetchChatProcesso = createAsyncThunk<
  { processoId: string; page: ChatListPage },
  { processoId: string; page?: number; limit?: number }
>('secretariaArbitral/fetchChatProcesso', async ({ processoId, page, limit }) => {
  const result = await secretariaArbitralService.listarChatProcesso(processoId, { page, limit });
  return { processoId, page: result };
});

export const fetchAlertasAtivos = createAsyncThunk<
  ArbitralAlertasAtivosPagina,
  ListarAlertasAtivosParams
>('secretariaArbitral/fetchAlertasAtivos', async (params) =>
  secretariaArbitralService.listarAlertasAtivos(params),
);

export const dispensarAlertasProcesso = createAsyncThunk<
  { processoId: string; motivo: ArbitralAlertaDispensaMotivo; alertaIds: string[] },
  { processoId: string; alertaIds: string[]; motivo: ArbitralAlertaDispensaMotivo }
>('secretariaArbitral/dispensarAlertasProcesso', async ({ processoId, alertaIds, motivo }) => {
  await Promise.all(alertaIds.map((id) => secretariaArbitralService.dispensarAlerta(id, motivo)));
  return { processoId, alertaIds, motivo };
});
